// Make changes › Starts with › Next number — "letters, the year, digits" in
// plain words, compiled to the designer's own SeriesConfig on an
// `auto_generate_prefixed()` default (twasta_runtime_engine.
// auto_generate_defaults). A series the designer built that these words
// cannot describe reads back as null, and the panel shows it read-only.

import type { SeriesConfig, SeriesPart } from '../../txn/types'
import { NEXT_NUMBER } from './fieldKinds'

export interface NextNumber {
  /** The letters the number starts with ("EC"); '' = none. */
  letters: string
  /** The year between the letters and the number. */
  year: '' | 'YYYY' | 'YY'
  digits: number
}

export const PREFIXED = 'auto_generate_prefixed()'
const PLAIN = /^\s*auto_generate\(\)\s*$/i
const IS_PREFIXED = /^\s*auto_generate_prefixed\(\)\s*$/i

export function defaultNextNumber(): NextNumber {
  return { letters: '', year: '', digits: NEXT_NUMBER.digits }
}

/** The default_value + series a Next number writes. A NUMBER field counts
 *  1, 2, 3… (`auto_generate()`); a text field gets letters / year / padded
 *  digits (`auto_generate_prefixed()`). */
export function nextNumberToModel(n: NextNumber, opts: { numeric?: boolean } = {}): {
  default_value: string; series: SeriesConfig | null
} {
  if (opts.numeric) return { default_value: 'auto_generate()', series: null }
  const letters = n.letters.trim().toUpperCase()
  const digits = Math.max(1, Math.min(12, Math.round(Number(n.digits) || NEXT_NUMBER.digits)))
  const parts: SeriesPart[] = []
  if (letters) parts.push({ source: 'const', text: `${letters}-` })
  if (n.year) {
    parts.push({ source: 'date', format: n.year })
    parts.push({ source: 'const', text: '-' })
  }
  return { default_value: PREFIXED, series: { parts, separator: '', width: digits } }
}

/** Read a field's numbering back into words; null when it is not one these
 *  words describe (the designer's own, richer series). */
export function nextNumberFromModel(col: { default_value?: string; series?: SeriesConfig | null }): NextNumber | null {
  const dv = String(col.default_value ?? '')
  if (PLAIN.test(dv)) return { letters: '', year: '', digits: col.series?.width ?? NEXT_NUMBER.digits }
  if (!IS_PREFIXED.test(dv)) return null
  const s = col.series || {}
  const parts = [...(s.parts || [])]
  if (s.separator) return null
  const out: NextNumber = { letters: '', year: '', digits: s.width ?? NEXT_NUMBER.digits }
  const first = parts[0]
  if (first && first.source === 'const' && /^[^-]*-$/.test(first.text || '')) {
    out.letters = (first.text || '').slice(0, -1)
    parts.shift()
  }
  if (parts.length === 2 && parts[0].source === 'date' && (parts[0].format === 'YYYY' || parts[0].format === 'YY')
      && parts[1].source === 'const' && parts[1].text === '-') {
    out.year = parts[0].format
    parts.splice(0, 2)
  }
  return parts.length ? null : out
}

/** Whether a default value is a numbering one (any kind). */
export function isNumbering(defaultValue: unknown): boolean {
  return /^\s*auto_generate[a-z_]*\(\)\s*$/i.test(String(defaultValue ?? ''))
}

/** What the next number will look like. */
export function nextNumberPreview(n: NextNumber, opts: { numeric?: boolean; now?: Date } = {}): string {
  const now = opts.now ?? new Date()
  const { series } = nextNumberToModel(n, opts)
  const digits = Math.max(1, Math.round(Number(n.digits) || NEXT_NUMBER.digits))
  if (!series) return '1'
  const num = '1'.padStart(digits, '0')
  const prefix = (series.parts || []).map((p) => {
    if (p.source === 'const') return p.text || ''
    if (p.source === 'date') {
      const y = String(now.getFullYear())
      return p.format === 'YY' ? y.slice(2) : y
    }
    return ''
  }).join('')
  return `${prefix}${num}`
}
